import React, { useCallback, useEffect } from "react";
import Header from "../components/Header";
import LicencesTable from "../components/LicencesTable";
import RenewModal from "../components/RenewModal";
import { useLicences } from "../hooks/useLicences";
import { useRenewFlow } from "../hooks/useRenewFlow";
import { AUTH_STORAGE_KEYS } from "../api/authApi";

export default function Licences() {
  const onUnauthorized = useCallback(() => {
    localStorage.removeItem(AUTH_STORAGE_KEYS.hasSession);
    window.location.href = "/login";
  }, []);

  const licences = useLicences({ onUnauthorized });
  const renew = useRenewFlow({ onUnauthorized });

  const { setError: setLicencesError } = licences;
  const { setError: setRenewError, setSuccess: setRenewSuccess } = renew;

  const errorMessage = renew.error || licences.error;
  const toast = errorMessage || renew.success;

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => {
      setLicencesError("");
      setRenewError("");
      setRenewSuccess("");
    }, 4000);
    return () => clearTimeout(timer);
  }, [toast, setLicencesError, setRenewError, setRenewSuccess]);

  const { open: isRenewOpen, close: closeRenew } = renew;

  useEffect(() => {
    if (!isRenewOpen) return undefined;
    const onEscape = (event) => {
      if (event.key === "Escape") closeRenew();
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [isRenewOpen, closeRenew]);

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {toast && (
        <div className="fixed top-4 right-4 z-50">
          <div
            className={`px-4 py-3 rounded-md shadow-lg text-sm ${
              errorMessage ? "bg-red-600 text-white" : "bg-green-600 text-white"
            }`}
          >
            {toast}
          </div>
        </div>
      )}

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute top-[70%] left-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="w-[36vw] h-[36vw] bg-[#ffbf3c] rounded-full blur-[12vw] floating-blob" />
        </div>
      </div>

      <Header />

        {licences.loading ? (
          <div className="flex items-center justify-center h-[745px]">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-100 border-t-[#ffbf3c]" />
          </div>
        ) : (
          <div className="relative w-full max-w-4xl mx-auto rounded-lg overflow-hidden border border-[#3D3935] ml-[2.4242vw] mr-[2.4242vw]">
            <LicencesTable
              pageItems={licences.pageItems}
              page={licences.page}
              totalPages={licences.totalPages}
              onGoToPage={licences.goToPage}
              renewingKey={renew.submitting ? renew.licence?.LicenseKey : null}
              onRenew={renew.start}
            />
          </div>
        )}

      <RenewModal
        open={renew.open}
        licence={renew.licence}
        years={renew.years}
        onYearsChange={renew.setYears}
        simultaneousCalls={renew.simultaneousCalls}
        onSimultaneousCallsChange={renew.setSimultaneousCalls}
        selectedEdition={renew.edition}
        onEditionChange={renew.setEdition}
        addHosting={renew.addHosting}
        onAddHostingChange={renew.setAddHosting}
        hostingLocked={renew.hostingLocked}
        keyInfoLoading={renew.keyInfoLoading}
        licenseDiscountPercent={licences.licenseDiscountPercent}
        hostingDiscountPercent={licences.hostingDiscountPercent}
        onClose={renew.close}
        onSubmit={renew.submit}
        submitting={renew.submitting}
        priceResult={renew.priceResult}
        priceLoading={renew.priceLoading}
        priceError={renew.priceError}
      />
    </div>
  );
}
